import { parsePastedNumber } from '@/helpers/pastedNumber.js'
import { roundHalfUp } from '@/helpers/roundHalfUp.js'
import { RV_CURRENCY_DIGITS, RV_RESIDUAL_FLOOR } from '@/data/constants.js'

// agreed cut-off (#2099 Q2/Q10), not derived from the period list: up to 15 the pasted times or rows
// are years, above it months; a table longer than 15 years needs a two-column paste in months
export const RV_PASTE_MAX_YEARS = 15

const lineError = (message, line) => ({ error: { message, params: { line } } })

const splitCells = (line) =>
    line
        .split(/[\t;]/)
        .map((cell) => cell.trim())
        .filter((cell) => cell !== '')

const groupsWithSpace = (locale) => /\s/.test(new Intl.NumberFormat(locale).format(1000))
const hasSpacedDigits = (cell, locale) => /\d\s+\d/.test(cell) && !groupsWithSpace(locale)

const THREE_DECIMAL_PERCENT = /^([-+]?\d{1,3})[.,](\d{3})$/

const readPercent = (text, locale) => {
    const threeDecimals = text.trim().match(THREE_DECIMAL_PERCENT)
    if (threeDecimals) {
        return Number(`${threeDecimals[1]}.${threeDecimals[2]}`)
    }
    return parsePastedNumber(text, locale, null, RV_CURRENCY_DIGITS)
}

// an amount, a % of the base, or Excel's accounting zero ("€ -")
const readValue = (cell, locale, currencyCode, baseAmount) => {
    if (cell.endsWith('%')) {
        const percent = readPercent(cell.slice(0, -1), locale)
        return percent === null ? null : (percent / 100) * baseAmount
    }
    if (!/\d/.test(cell) && cell.includes('-')) {
        return parsePastedNumber(cell.replace('-', '0'), locale, currencyCode, RV_CURRENCY_DIGITS)
    }
    return parsePastedNumber(cell, locale, currencyCode, RV_CURRENCY_DIGITS)
}

const readTime = (cell, locale) => parsePastedNumber(cell, locale, null, RV_CURRENCY_DIGITS)

const fromValues = (rows) => {
    const inYears = rows.length <= RV_PASTE_MAX_YEARS
    return {
        inYears,
        points: rows.map((row, i) => ({ months: inYears ? (i + 1) * 12 : i + 1, value: row.numbers[0] })),
    }
}

const fromTimes = (rows) => {
    let previous = -1
    for (const row of rows) {
        const time = row.numbers[0]
        if (!Number.isInteger(time) || time <= previous) {
            return lineError('§contracts.rv_paste_time_invalid', row.line)
        }
        previous = time
    }
    const inYears = previous <= RV_PASTE_MAX_YEARS
    return {
        inYears,
        points: rows.map((row) => ({
            months: inYears ? row.numbers[0] * 12 : row.numbers[0],
            value: row.numbers[1],
        })),
    }
}

export const parseRvPaste = (text, locale, currencyCode, baseAmount) => {
    const lines = (text ?? '').split(/\r?\n/)
    const lastFilled = lines.findLastIndex((line) => splitCells(line).length > 0)
    const rows = []
    let columns = null

    for (const [index, line] of lines.entries()) {
        const cells = splitCells(line)
        if (cells.length === 0) {
            // a gap inside one column would shift every later value by a row
            if (columns === 1 && index < lastFilled) {
                return lineError('§contracts.rv_paste_line_empty', index + 1)
            }
            continue
        }

        if (cells.some((cell) => hasSpacedDigits(cell, locale))) {
            return lineError('§contracts.rv_paste_line_unreadable', index + 1)
        }

        const numbers = cells.map((cell, i) =>
            cells.length === 2 && i === 0
                ? readTime(cell, locale)
                : readValue(cell, locale, currencyCode, baseAmount),
        )
        if (numbers.includes(null)) {
            // only a line with no number at all, before the first number line, is a header
            if (rows.length === 0 && numbers.every((number) => number === null)) {
                continue
            }
            return lineError('§contracts.rv_paste_line_unreadable', index + 1)
        }

        if (cells.length > 2 || (columns !== null && cells.length !== columns)) {
            return lineError('§contracts.rv_paste_column_count', index + 1)
        }

        columns = cells.length
        rows.push({ line: index + 1, numbers })
    }

    if (rows.length === 0) {
        return { error: { message: '§contracts.rv_paste_no_values', params: {} } }
    }

    return columns === 1 ? fromValues(rows) : fromTimes(rows)
}

const belowFloor = (value) => value >= 0 && value < RV_RESIDUAL_FLOOR

const floorTrailingZeros = (points) => {
    const lastAbove = points.findLastIndex((point) => !belowFloor(point.value))
    return points.map((point, i) => (i > lastAbove ? { ...point, value: RV_RESIDUAL_FLOOR } : point))
}

export const fillRvTable = (table, pastedPoints, baseAmount) => {
    const points = floorTrailingZeros(pastedPoints)
    const tableMonths = new Set(table.map((row) => row.months))
    const landed = points.filter((point) => tableMonths.has(point.months))
    if (landed.length === 0) {
        return { error: { message: '§contracts.rv_paste_no_matching_rows', params: {} } }
    }

    const byMonth = new Map(points.map((point) => [point.months, point.value]))
    const lastMonth = Math.max(...points.map((point) => point.months))
    const firstAfterStart = points.find((point) => point.months > 0)

    const rows = table.map((row) => {
        let value
        if (byMonth.has(row.months)) {
            value = byMonth.get(row.months)
        } else if (row.months === 0) {
            value = baseAmount
        } else if (row.months === 1 && firstAfterStart) {
            value = firstAfterStart.value
        } else if (row.months > lastMonth) {
            value = RV_RESIDUAL_FLOOR
        } else {
            return row
        }
        return { ...row, '100%': roundHalfUp(value, RV_CURRENCY_DIGITS) }
    })

    const lastRow = table[table.length - 1].months
    return {
        rows,
        read: {
            count: points.length,
            first: points[0].months,
            last: lastMonth,
            beyondTable: lastMonth > lastRow,
            lastRow,
        },
    }
}
